import {
  User,
  Mail,
  ExternalLink,
  Clock,
  Calendar,
  Tag,
  MessageSquare,
  Check,
  X,
  AlertCircle,
  ChevronDown,
} from "lucide-react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { adminProfilePathForRole } from "@/lib/adminProfilePaths";
import {
  Button,
  buttonVariants,
  DefinitionList,
  DetailRow,
  DetailSectionTitle,
  fieldControlClass,
  Label,
  TruncatedText,
} from "@/components/ui";
import type { SupportTicket } from "@/services/supportTicketService";
import {
  STATUS_CONFIG,
  PRIORITY_CONFIG,
  CATEGORY_CONFIG,
  formatFullDate,
  ticketStatusLabel,
  ticketPriorityLabel,
  ticketCategoryLabel,
  TicketStatus,
  TicketPriority,
} from "./config";

interface TicketSidebarProps {
  ticket: Pick<
    SupportTicket,
    | "status"
    | "priority"
    | "category"
    | "owner_user_id"
    | "owner_name"
    | "owner_email"
    | "owner_role"
    | "created_at"
    | "updated_at"
    | "resolved_at"
    | "message_count"
  >;
  onStatusChange: (status: TicketStatus) => void;
  onPriorityChange: (priority: TicketPriority) => void;
  isUpdating: boolean;
  /** Admin ticket detail: link to the owner's portal profile. */
  showOwnerProfileLink: boolean;
}

export function TicketSidebar({
  ticket,
  onStatusChange,
  onPriorityChange,
  isUpdating,
  showOwnerProfileLink,
}: TicketSidebarProps) {
  const { t } = useTranslation("support");
  const statusConfig = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.closed;
  const StatusIcon = statusConfig.icon;
  const categoryConfig = CATEGORY_CONFIG[ticket.category] ?? CATEGORY_CONFIG.question;
  const profilePath = showOwnerProfileLink
    ? adminProfilePathForRole(
        ticket.owner_user_id,
        ticket.owner_role ?? undefined,
      )
    : null;
  const ownerDisplayName =
    ticket.owner_name || t("sidebar.unknownSeller");

  return (
    <div className="min-w-0 divide-y divide-surface-200/70">
      {/* Owner Info */}
      <div className="min-w-0 px-5 py-4">
        <DetailSectionTitle as="h3" className="mb-3">
          {t("sidebar.seller")}
        </DetailSectionTitle>
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-600/15">
            <User className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            {profilePath ? (
              <Link
                to={profilePath}
                className="block text-sm font-semibold text-surface-900 transition-colors hover:text-primary-700"
              >
                <TruncatedText>{ownerDisplayName}</TruncatedText>
              </Link>
            ) : (
              <TruncatedText
                as="p"
                className="text-sm font-semibold text-surface-900"
              >
                {ownerDisplayName}
              </TruncatedText>
            )}
            {ticket.owner_email ? (
              <TruncatedText as="p" className="text-[13px] text-surface-500">
                {ticket.owner_email}
              </TruncatedText>
            ) : null}
            {ticket.owner_role ? (
              <TruncatedText
                as="p"
                className="mt-0.5 font-mono text-[11px] text-surface-400"
              >
                {ticket.owner_role}
              </TruncatedText>
            ) : null}
          </div>
        </div>
        <div className="mt-3 grid gap-2">
          {profilePath && (
            <Link
              to={profilePath}
              className={buttonVariants({ variant: "outline", className: "w-full min-w-0" })}
            >
              <User className="h-4 w-4 shrink-0" />
              <TruncatedText>{t("sidebar.openProfile")}</TruncatedText>
            </Link>
          )}
          {ticket.owner_email && (
            <a
              href={`mailto:${ticket.owner_email}`}
              className={buttonVariants({ variant: "ghost", className: "w-full min-w-0" })}
            >
              <Mail className="h-4 w-4 shrink-0" />
              <TruncatedText>{t("sidebar.sendEmail")}</TruncatedText>
              <ExternalLink className="h-3 w-3 shrink-0 text-surface-400" />
            </a>
          )}
        </div>
      </div>

      {/* Status & Priority */}
      <div className="min-w-0 px-5 py-4">
        <DetailSectionTitle as="h3" className="mb-3">
          {t("sidebar.statusAndPriority")}
        </DetailSectionTitle>
        <div className="min-w-0 space-y-3">
          <div>
            <Label htmlFor="ticket-status" spacing="field">
              {t("sidebar.status")}
            </Label>
            <div className="relative">
              <StatusIcon
                className={cn(
                  "pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2",
                  statusConfig.color,
                )}
                aria-hidden
              />
              <select
                id="ticket-status"
                value={ticket.status}
                onChange={(e) => onStatusChange(e.target.value as TicketStatus)}
                disabled={isUpdating}
                className={fieldControlClass({
                  className: cn(
                    "max-w-full appearance-none pl-8 pr-9 font-medium",
                    statusConfig.color,
                  ),
                })}
              >
                {Object.entries(STATUS_CONFIG).map(([status]) => (
                  <option
                    key={status}
                    value={status}
                  >
                    {ticketStatusLabel(status as TicketStatus)}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
            </div>
          </div>

          <div>
            <Label htmlFor="ticket-priority" spacing="field">
              {t("sidebar.priority")}
            </Label>
            <div className="relative">
              <select
                id="ticket-priority"
                value={ticket.priority}
                onChange={(e) =>
                  onPriorityChange(parseInt(e.target.value) as TicketPriority)
                }
                disabled={isUpdating}
                className={fieldControlClass({
                  className: "max-w-full appearance-none pr-9 font-medium",
                })}
              >
                {Object.entries(PRIORITY_CONFIG).map(([priority]) => (
                  <option
                    key={priority}
                    value={priority}
                  >
                    {ticketPriorityLabel(
                      parseInt(priority, 10) as TicketPriority,
                    )}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="min-w-0 px-5 py-4">
        <DetailSectionTitle as="h3" className="mb-3">
          {t("sidebar.details")}
        </DetailSectionTitle>
        <DefinitionList className="space-y-2.5">
          <DetailRow icon={Tag} label={t("sidebar.category")}>
            <TruncatedText as="span" className="font-medium">
              {`${categoryConfig.emoji} ${ticketCategoryLabel(ticket.category)}`}
            </TruncatedText>
          </DetailRow>
          <DetailRow icon={Calendar} label={t("sidebar.created")}>
            <TruncatedText as="span" className="font-medium">
              {formatFullDate(ticket.created_at)}
            </TruncatedText>
          </DetailRow>
          <DetailRow icon={Clock} label={t("sidebar.lastUpdated")}>
            <TruncatedText as="span" className="font-medium">
              {formatFullDate(ticket.updated_at)}
            </TruncatedText>
          </DetailRow>
          {ticket.resolved_at ? (
            <DetailRow icon={Check} label={t("sidebar.resolved")}>
              <TruncatedText as="span" className="font-medium">
                {formatFullDate(ticket.resolved_at)}
              </TruncatedText>
            </DetailRow>
          ) : null}
          <DetailRow icon={MessageSquare} label={t("sidebar.messages")}>
            <TruncatedText as="span" className="font-medium">
              {t("sidebar.messagesTotal", { count: ticket.message_count })}
            </TruncatedText>
          </DetailRow>
        </DefinitionList>
      </div>

      {/* Quick Actions */}
      <div className="min-w-0 px-5 py-4">
        <DetailSectionTitle as="h3" className="mb-3">
          {t("sidebar.quickActions")}
        </DetailSectionTitle>
        <div className="min-w-0 space-y-2">
          {ticket.status !== TicketStatus.RESOLVED &&
            ticket.status !== TicketStatus.CLOSED && (
              <Button
                variant="outline"
                onClick={() => onStatusChange(TicketStatus.RESOLVED)}
                disabled={isUpdating}
                className="w-full justify-center hover:border-success-300 hover:bg-success-50 hover:text-success-700 dark:hover:bg-success-50"
              >
                <Check className="h-4 w-4 shrink-0 text-success-600" />
                <TruncatedText>{t("sidebar.markAsResolved")}</TruncatedText>
              </Button>
            )}
          {ticket.status !== TicketStatus.CLOSED && (
            <Button
              variant="outline"
              onClick={() => onStatusChange(TicketStatus.CLOSED)}
              disabled={isUpdating}
              className="w-full justify-center"
            >
              <X className="h-4 w-4 shrink-0 text-surface-400" />
              <TruncatedText>{t("sidebar.closeTicket")}</TruncatedText>
            </Button>
          )}
          {ticket.status === TicketStatus.CLOSED && (
            <Button
              variant="outline"
              onClick={() => onStatusChange(TicketStatus.OPEN)}
              disabled={isUpdating}
              className="w-full justify-center"
            >
              <AlertCircle className="h-4 w-4 shrink-0 text-primary-600" />
              <TruncatedText>{t("sidebar.reopenTicket")}</TruncatedText>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
